import { useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, FileText, Printer, Upload } from 'lucide-react';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  FieldGroup,
  Input,
  Spinner,
} from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { uploadMedia } from '@/lib/upload';
import type { ChildSummary } from '@/types';

interface DocumentType {
  id: string;
  code: string;
  name: string;
  scope: 'CHILD' | 'FACILITY';
  category: string | null;
  retentionYears: number;
  inspectionTarget: boolean;
}

interface UploadedDocument {
  id: string;
  title: string;
  year: number;
  status: 'ACTIVE' | 'EXIT_ARCHIVED' | 'PURGED';
  purgeDueAt: string | null;
  createdAt: string;
  type: DocumentType;
}

interface SystemDocument {
  kind: 'medication' | 'pickup' | 'lifeRecord' | 'development';
  id: string;
  typeCode: string;
  title: string;
  date: string;
  hasFile: boolean;
}

interface ChildBox {
  child: { id: string; name: string; exited: boolean };
  uploads: UploadedDocument[];
  system: SystemDocument[];
}

type MergeItem = { kind: 'upload' | SystemDocument['kind']; id: string };

/**
 * 전자 서류함 — 명세서 §5.22 (P4 기본)
 *
 * 원아별 서류함은 업로드 편철 + 시스템 전자문서(원본 참조 자동 편철)를 합쳐 보여 주고,
 * 다중 선택으로 병합 PDF를 만든다. 평소엔 무출력 전자 보관이 전제다.
 */
export function DocumentsPage() {
  const [tab, setTab] = useState<'child' | 'facility' | 'renewals'>('child');

  return (
    <Screen>
      <AppBar title="전자 서류함" back />

      <div className="px-4 pt-3">
        <div className="flex gap-2">
          <Chip tone={tab === 'child' ? 'primary' : 'neutral'} selected={tab === 'child'} onClick={() => setTab('child')}>
            원아별
          </Chip>
          <Chip
            tone={tab === 'facility' ? 'primary' : 'neutral'}
            selected={tab === 'facility'}
            onClick={() => setTab('facility')}
          >
            시설
          </Chip>
          <Chip
            tone={tab === 'renewals' ? 'primary' : 'neutral'}
            selected={tab === 'renewals'}
            onClick={() => setTab('renewals')}
          >
            수합
          </Chip>
        </div>
      </div>

      {tab === 'child' && <ChildBoxTab />}
      {tab === 'facility' && <FacilityBoxTab />}
      {tab === 'renewals' && <RenewalsTab />}
    </Screen>
  );
}

/** 병합류 액션 버튼 — 결과 URL을 받으면 열기 버튼으로 바뀐다 */
function BundleButton({
  label,
  request,
}: {
  label: string;
  request: () => Promise<{ url: string }>;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <Button block variant="outline" icon={<Printer size={16} />}>
          {label} 열기
        </Button>
      </a>
    );
  }

  return (
    <div>
      <Button
        block
        variant="secondary"
        icon={<Printer size={16} />}
        loading={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          request()
            .then((result) => setUrl(result.url))
            .catch((caught) =>
              setError(caught instanceof ApiError ? caught.message : '만들지 못했어요.'),
            )
            .finally(() => setBusy(false));
        }}
      >
        {label}
      </Button>
      {error && <p className="mt-1 text-sm text-notice">{error}</p>}
    </div>
  );
}

/** 지원금 정산 패키지 (§5.22 P5) — 정산 카테고리 체크리스트 + 연도 일괄 출력 */
function SettlementCard() {
  const [year, setYear] = useState(new Date().getFullYear());

  const { data } = useQuery({
    queryKey: ['documents', 'settlement', year],
    queryFn: () =>
      apiData<{ year: number; checklist: { typeCode: string; name: string; count: number }[] }>(
        `/documents/settlement-status?year=${year}`,
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <Card>
      <div className="mb-2 flex items-center gap-2">
        <p className="flex-1 text-sm font-semibold">지원금 정산 서류 ({year}년)</p>
        <Button size="sm" variant="ghost" onClick={() => setYear((y) => y - 1)}>
          ◀
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setYear((y) => y + 1)}>
          ▶
        </Button>
      </div>

      <ul className="mb-3 flex flex-col gap-1 text-sm">
        {(data?.checklist ?? []).map((item) => (
          <li key={item.typeCode} className="flex items-center justify-between">
            <span>{item.name}</span>
            <span className={item.count > 0 ? 'text-primary-strong' : 'text-ink-sub'}>
              {item.count > 0 ? `${item.count}건` : '없음'}
            </span>
          </li>
        ))}
      </ul>

      <BundleButton
        label={`${year}년 정산 서류 일괄 PDF`}
        request={() =>
          apiData<{ url: string }>('/documents/settlement-bundle', { method: 'POST', body: { year } })
        }
      />
      <p className="mt-2 text-sm text-ink-sub">
        정산 금액 계산·회계 처리는 다루지 않아요. 증빙 서류의 보관과 출력까지만 돕습니다.
      </p>
    </Card>
  );
}

/** 연차 갱신 수합 현황 — 미제출이 위로, 제출분은 확인·편철 (§5.22 P5) */
function RenewalsTab() {
  const queryClient = useQueryClient();
  const [typeCode, setTypeCode] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const types = useQuery({
    queryKey: ['documents', 'types'],
    queryFn: () => apiData<DocumentType[]>('/documents/types'),
  });
  const renewable = (types.data ?? []).filter((t) => t.scope === 'CHILD');
  const activeType = typeCode || renewable[0]?.code || '';

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['documents', 'renewals'],
    queryFn: () =>
      apiData<
        {
          id: string;
          status: 'REQUESTED' | 'SUBMITTED' | 'CONFIRMED';
          dueDate: string;
          type: { name: string };
          child: { id: string; name: string };
        }[]
      >('/documents/renewals'),
  });

  const generate = useMutation({
    mutationFn: () =>
      apiData<{ created: number; total: number }>('/documents/renewals/generate', {
        method: 'POST',
        body: { typeCode: activeType, dueDate },
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['documents', 'renewals'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '요청을 만들지 못했어요.'),
  });

  const confirm = useMutation({
    mutationFn: (id: string) => apiData(`/documents/renewals/${id}/confirm`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['documents'] }),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '확인하지 못했어요.'),
  });

  const rows = data ?? [];
  const pending = rows.filter((r) => r.status !== 'CONFIRMED');

  return (
    <div className="flex flex-col gap-3 p-4">
      <Card>
        <p className="mb-2 text-sm font-semibold">수합 요청 만들기 (재원 원아 전원)</p>
        <FieldGroup label="서류 유형">
          <div className="flex flex-wrap gap-1.5">
            {renewable.map((type) => (
              <Chip
                key={type.code}
                tone={type.code === activeType ? 'primary' : 'neutral'}
                selected={type.code === activeType}
                onClick={() => setTypeCode(type.code)}
              >
                {type.name}
              </Chip>
            ))}
          </div>
        </FieldGroup>
        <Field label="제출 기한">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>
        {error && <p className="mb-2 text-sm text-notice">{error}</p>}
        <Button block disabled={!activeType || !dueDate} loading={generate.isPending} onClick={() => generate.mutate()}>
          제출 요청 보내기
        </Button>
      </Card>

      {isPending && <Spinner />}
      {isError && <ErrorNote message="수합 현황을 불러오지 못했어요." onRetry={() => void refetch()} />}

      {rows.length > 0 && (
        <p className="px-1 text-sm text-ink-sub">
          미제출·확인 대기 <b className="tnum">{pending.length}</b>건 / 전체{' '}
          <b className="tnum">{rows.length}</b>건
        </p>
      )}

      {rows.map((row) => (
        <Card key={row.id} className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-medium">
              {row.child.name} · {row.type.name}
            </p>
            <p className="text-sm text-ink-sub">기한 {formatDate(row.dueDate)}</p>
          </div>
          {row.status === 'REQUESTED' && <Chip tone="secondary">미제출</Chip>}
          {row.status === 'SUBMITTED' && (
            <Button size="sm" loading={confirm.isPending} onClick={() => confirm.mutate(row.id)}>
              확인·편철
            </Button>
          )}
          {row.status === 'CONFIRMED' && <Chip tone="primary">편철 완료</Chip>}
        </Card>
      ))}
    </div>
  );
}

function ChildBoxTab() {
  const [selectedChild, setSelectedChild] = useState('');
  const [selection, setSelection] = useState<MergeItem[]>([]);
  const [mergeUrl, setMergeUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const { data: children } = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });
  const childId = selectedChild || children?.[0]?.id || '';

  const box = useQuery({
    queryKey: ['documents', 'child', childId],
    queryFn: () => apiData<ChildBox>(`/documents/child/${childId}`),
    enabled: Boolean(childId),
    placeholderData: keepPreviousData,
  });

  const merge = useMutation({
    mutationFn: () =>
      apiData<{ url: string }>('/documents/merge', { method: 'POST', body: { items: selection } }),
    onSuccess: (result) => {
      setMergeUrl(result.url);
      setError(null);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '병합하지 못했어요.'),
  });

  const toggle = (item: MergeItem) => {
    setMergeUrl(null);
    setSelection((prev) =>
      prev.some((s) => s.kind === item.kind && s.id === item.id)
        ? prev.filter((s) => !(s.kind === item.kind && s.id === item.id))
        : [...prev, item],
    );
  };
  const isSelected = (item: MergeItem) =>
    selection.some((s) => s.kind === item.kind && s.id === item.id);

  return (
    <div className="flex flex-col gap-3 p-4">
      {(children?.length ?? 0) > 1 && (
        <div className="flex flex-wrap gap-2">
          {children?.map((child) => (
            <Chip
              key={child.id}
              tone={child.id === childId ? 'primary' : 'neutral'}
              selected={child.id === childId}
              onClick={() => {
                setSelectedChild(child.id);
                setSelection([]);
                setMergeUrl(null);
              }}
            >
              {child.name}
            </Chip>
          ))}
        </div>
      )}

      {box.isPending && childId && <Spinner />}
      {box.isError && <ErrorNote message="서류함을 불러오지 못했어요." onRetry={() => void box.refetch()} />}

      {box.data && (
        <>
          {box.data.child.exited && (
            <p className="flex items-center gap-1.5 rounded-sm bg-accent-soft px-3 py-2 text-sm">
              <Archive size={14} /> 퇴소 서류철이에요. 보존 만료일이 지나면 파기 대상이 됩니다.
            </p>
          )}

          <UploadForm childId={childId} onBusy={setUploading} />

          {box.data.uploads.length === 0 && box.data.system.length === 0 && !uploading && (
            <EmptyState title="서류함이 비어 있어요" body="종이 서류를 촬영해 올리면 여기에 편철돼요." />
          )}

          {box.data.uploads.map((doc) => (
            <DocumentRow
              key={doc.id}
              title={doc.title}
              subtitle={`${doc.type.name} · ${doc.year}년${doc.purgeDueAt ? ` · 보존 ${formatDate(doc.purgeDueAt)}까지` : ''}`}
              selected={isSelected({ kind: 'upload', id: doc.id })}
              onToggle={() => toggle({ kind: 'upload', id: doc.id })}
            />
          ))}

          {box.data.system.length > 0 && (
            <p className="px-1 pt-1 text-sm font-semibold text-ink-sub">자동 편철 (전자 원본)</p>
          )}
          {box.data.system.map((doc) => (
            <DocumentRow
              key={`${doc.kind}-${doc.id}`}
              title={doc.title}
              subtitle={doc.date}
              selected={isSelected({ kind: doc.kind, id: doc.id })}
              onToggle={() => toggle({ kind: doc.kind, id: doc.id })}
            />
          ))}

          {error && <p className="text-sm text-notice">{error}</p>}

          {mergeUrl ? (
            <a href={mergeUrl} target="_blank" rel="noreferrer">
              <Button block icon={<Printer size={16} />}>
                서류철 PDF 열기
              </Button>
            </a>
          ) : (
            <Button
              block
              variant="secondary"
              icon={<Printer size={16} />}
              disabled={selection.length === 0}
              loading={merge.isPending}
              onClick={() => merge.mutate()}
            >
              선택한 {selection.length}건 병합 PDF
            </Button>
          )}

          <BundleButton
            label="지도점검 묶음 PDF (이 아이)"
            request={() =>
              apiData<{ url: string }>('/documents/inspection-bundle', {
                method: 'POST',
                body: { childId },
              })
            }
          />
        </>
      )}
    </div>
  );
}

function DocumentRow({
  title,
  subtitle,
  selected,
  onToggle,
}: {
  title: string;
  subtitle: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onToggle}
      className={`flex w-full items-center gap-3 rounded-md bg-surface p-3.5 text-left shadow-card ${
        selected ? 'ring-2 ring-primary' : ''
      }`}
    >
      <FileText size={18} className="shrink-0 text-primary-strong" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-medium">{title}</span>
        <span className="block truncate text-sm text-ink-sub">{subtitle}</span>
      </span>
    </button>
  );
}

/** 종이 수합분 촬영·스캔 업로드 편철 */
function UploadForm({ childId, onBusy }: { childId?: string; onBusy?: (busy: boolean) => void }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [typeCode, setTypeCode] = useState('');
  const [title, setTitle] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const types = useQuery({
    queryKey: ['documents', 'types'],
    queryFn: () => apiData<DocumentType[]>('/documents/types'),
  });

  const scoped = (types.data ?? []).filter((t) => (childId ? t.scope === 'CHILD' : t.scope === 'FACILITY'));
  const activeType = typeCode || scoped[0]?.code || '';

  const handleUpload = async (file: File) => {
    setBusy(true);
    onBusy?.(true);
    setError(null);
    try {
      const mediaId = await uploadMedia(file);
      await apiData('/documents', {
        method: 'POST',
        body: { typeCode: activeType, title: title.trim() || file.name, mediaId, childId },
      });
      setTitle('');
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '편철하지 못했어요.');
    } finally {
      setBusy(false);
      onBusy?.(false);
    }
  };

  if (!open) {
    return (
      <Button variant="outline" icon={<Upload size={16} />} onClick={() => setOpen(true)}>
        서류 올리기
      </Button>
    );
  }

  return (
    <Card>
      <FieldGroup label="서류 유형">
        <div className="flex flex-wrap gap-1.5">
          {scoped.map((type) => (
            <Chip
              key={type.code}
              tone={type.code === activeType ? 'primary' : 'neutral'}
              selected={type.code === activeType}
              onClick={() => setTypeCode(type.code)}
            >
              {type.name}
            </Chip>
          ))}
        </div>
      </FieldGroup>

      <Field label="제목 (선택)">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="비우면 파일 이름을 써요" />
      </Field>

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleUpload(file);
          e.target.value = '';
        }}
      />

      {error && <p className="mb-2 text-sm text-notice">{error}</p>}

      <div className="flex gap-2">
        <Button variant="outline" block onClick={() => setOpen(false)}>
          닫기
        </Button>
        <Button block loading={busy} disabled={!activeType} onClick={() => fileRef.current?.click()}>
          파일 선택·편철
        </Button>
      </div>
    </Card>
  );
}

function FacilityBoxTab() {
  const [category, setCategory] = useState('');

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['documents', 'facility', category],
    queryFn: () =>
      apiData<UploadedDocument[]>(`/documents/facility${category ? `?category=${category}` : ''}`),
    placeholderData: keepPreviousData,
  });

  const categories = ['운영', '안전', '점검', '정산', '건강'];

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap gap-1.5">
        <Chip tone={category === '' ? 'primary' : 'neutral'} selected={category === ''} onClick={() => setCategory('')}>
          전체
        </Chip>
        {categories.map((item) => (
          <Chip
            key={item}
            tone={category === item ? 'primary' : 'neutral'}
            selected={category === item}
            onClick={() => setCategory(item)}
          >
            {item}
          </Chip>
        ))}
      </div>

      <UploadForm />

      <BundleButton
        label="지도점검 묶음 PDF (시설 전체)"
        request={() =>
          apiData<{ url: string }>('/documents/inspection-bundle', { method: 'POST', body: {} })
        }
      />
      <SettlementCard />

      {isPending && <Spinner />}
      {isError && <ErrorNote message="서류함을 불러오지 못했어요." onRetry={() => void refetch()} />}

      {(data?.length ?? 0) === 0 && !isPending && (
        <EmptyState title="보관된 서류가 없어요" body="운영·안전·점검 서류를 올려 전자로 보관해 보세요." />
      )}

      {data?.map((doc) => (
        <Card key={doc.id} className="flex items-center gap-3">
          <FileText size={18} className="shrink-0 text-primary-strong" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-medium">{doc.title}</p>
            <p className="truncate text-sm text-ink-sub">
              {doc.type.name} · {doc.year}년{doc.type.inspectionTarget ? ' · 점검 제출 대상' : ''}
            </p>
          </div>
        </Card>
      ))}
    </div>
  );
}
